import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { adminApi } from '../../services/api';
import LHCFormViewer from '../../components/forms/LHCFormViewer';
import SurveyJSFormEditor from '../../components/forms/SurveyJSFormEditor';

const FormPreviewPage = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        setIsLoading(true);
        const urlParams = new URLSearchParams(window.location.search);
        const version = urlParams.get('version');

        const response = await adminApi.getFormDetails(formId, version);

        if (response.data.success) {
          setForm(response.data.form);
        } else {
          setError(response.data.message || 'Failed to load form for preview');
        }
      } catch (err) {
        if (err.response && err.response.status === 403) {
          setError('Permission denied. This feature requires proper authentication and authorization.');
        } else {
          setError(`Error loading form for preview: ${err.message}`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (formId) {
      fetchForm();
    } else {
      setError('Form ID is missing.');
      setIsLoading(false);
    }
  }, [formId]);

  if (isLoading) {
    return (
      <>
        <div className="d-flex justify-content-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="alert alert-danger mt-4">{error}</div>
      </>
    );
  }

  if (!form) {
    return (
      <>
        <div className="alert alert-info mt-4">Form not found.</div>
      </>
    );
  }

  return (
    <>
      <style>
        {`.svc-creator__banner { display: none !important; }`}
      </style>

      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between"
                        onClick={() => setShowDetails(!showDetails)}>
            <div className="d-flex align-items-center">
              <button
                className="btn btn-sm btn-outline-secondary me-3"
                onClick={() => navigate('/admin/forms')}
                title="Back to Forms"
              >
                <i className="fas fa-arrow-left"></i>
              </button>
              <h4 className="mb-0 font-size-18">{form.name}</h4>
            </div>
            <div className="page-title-right d-flex align-items-center gap-2">
              <h4 className="badge bg-primary">Form ID {form.form_id}</h4>
              <span className="badge bg-primary">Version {form.version}</span>
              <span className="badge bg-info">{form.type.name}</span>

              <span className="btn btn-outline-secondary btn-sm">
                <i className={`fas ${showDetails ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible Form Details */}
      {showDetails && (
        <div className="card">
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <p className="mb-2"><strong>Form ID:</strong> {form.form_id}</p>
                <p className="mb-2"><strong>Version:</strong> {form.version}</p>
                <p className="mb-2"><strong>Type:</strong> {form.type.name}</p>
                <p className="mb-2"><strong>Status:</strong> {form.is_active ?
                  <span className="badge bg-success">Active</span> :
                  <span className="badge bg-secondary">Inactive</span>
                }</p>
              </div>
              <div className="col-md-8">
                <p className="mb-2"><strong>Description:</strong> {form.description || 'N/A'}</p>
                <p className="mb-2"><strong>Health Systems:</strong> {
                  form.health_systems && form.health_systems.length > 0
                    ? form.health_systems.map(hs => hs.name).join(', ')
                    : 'N/A'
                }</p>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12">
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/forms/edit/${form.form_id}?version=${form.version}`);
                  }}
                >
                  <i className="fas fa-edit me-1"></i>
                  Edit Form
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Preview */}
      {form.type.name.toLowerCase() === 'lhc form' || form.type.name.toLowerCase().startsWith('lhc ') ? (
        <LHCFormViewer schema={form.schema} />
      ) : (
        <SurveyJSFormEditor schema={form.schema} theme={form.theme} previewMode={true} />
      )}
    </>
  );
};

export default FormPreviewPage;
